'use client';
import React, { useState, useMemo, useEffect } from 'react';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  label: string;
  icon: string;
  color: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'visitor-booking',
    label: 'Visitor Booking',
    icon: 'CalendarDaysIcon',
    color: 'text-secondary',
    items: [
      {
        question: 'How far in advance can I book a tee time?',
        answer:
          'Tee times can be booked up to 14 days in advance online. Members may book up to 21 days ahead. Walk-in bookings are subject to availability on the day.',
      },
      {
        question: 'Can I book for a group or society?',
        answer:
          'Yes. Groups of 5 or more and golf societies should contact the pro shop directly at 033 396 2356 or email golf@maritzburggolfclub.co.za. We accommodate groups of up to 40 players and can arrange shotgun starts for larger events.',
      },
      {
        question: 'What happens if I need to cancel or reschedule?',
        answer:
          'Cancellations made more than 24 hours before your tee time receive a full refund. Cancellations within 24 hours are non-refundable. To reschedule, cancel your existing booking and create a new one — subject to availability.',
      },
      {
        question: 'How many players can I book per tee time?',
        answer:
          'Each tee time accommodates 1 to 4 players. If your group is larger than 4, you will need to book consecutive tee times (typically 8–10 minutes apart).',
      },
      {
        question: 'Can I book a tee time without creating an account?',
        answer:
          'Guest bookings are available for single rounds. However, creating a free account lets you view booking history, earn loyalty points, and receive exclusive member offers.',
      },
      {
        question: 'What is the latest I can book on the day?',
        answer:
          'Same-day online bookings close 2 hours before the desired tee time. After that, please call the pro shop directly to check availability.',
      },
    ],
  },
  {
    id: 'membership',
    label: 'Membership',
    icon: 'StarIcon',
    color: 'text-purple-500',
    items: [
      {
        question: 'What membership options are available?',
        answer:
          'We offer Full Member, Social Member, Junior Member (under 21), and Senior Member (65+) categories. Each comes with different green fee rates, booking priority, and access to club facilities. Contact the club for current annual subscription rates.',
      },
      {
        question: 'How do I apply for membership?',
        answer:
          'Membership applications are submitted in person at the club or by emailing golf@maritzburggolfclub.co.za. Applications are reviewed by the membership committee and typically processed within 2 weeks.',
      },
      {
        question: 'What is the Loyalty Programme?',
        answer:
          'Our Loyalty Programme rewards regular players with points for every round played. Points can be redeemed for discounted green fees, pro shop vouchers, and exclusive club events. Points are automatically tracked when you book with your registered account.',
      },
      {
        question: 'Do members get discounted green fees?',
        answer:
          'Yes. Full Members play at a significantly reduced rate compared to visitor green fees. Members also enjoy priority booking windows and access to member-only competitions throughout the year.',
      },
      {
        question: 'Can I transfer my membership if I move away?',
        answer:
          'Memberships are non-transferable. If you relocate, you may apply for a temporary suspension of your membership for up to 12 months. Please contact the club secretary for details.',
      },
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: 'CreditCardIcon',
    color: 'text-accent',
    items: [
      {
        question: 'What payment methods are accepted?',
        answer:
          'We accept all major credit and debit cards (Visa, Mastercard), EFT, and instant EFT via PayFast. Cash payments are accepted at the pro shop on the day.',
      },
      {
        question: 'Is my payment information secure?',
        answer:
          'All online payments are processed through PayFast, a PCI-DSS compliant payment gateway. We never store your card details on our servers.',
      },
      {
        question: 'When is payment taken for my booking?',
        answer:
          'Payment is taken in full at the time of booking. You will receive an instant confirmation email with your booking reference and payment receipt.',
      },
      {
        question: 'Can I pay on the day instead of online?',
        answer:
          'Walk-in players may pay at the pro shop. However, online pre-payment guarantees your tee time and is required for all advance bookings made through this website.',
      },
      {
        question: 'How do refunds work?',
        answer:
          'Approved refunds are processed back to your original payment method within 3–5 business days via PayFast. You will receive an email confirmation once the refund has been initiated.',
      },
      {
        question: 'Are there any booking fees or surcharges?',
        answer:
          'There are no additional booking fees. The price displayed at checkout is the final amount charged — green fees only, with no hidden surcharges.',
      },
    ],
  },
  {
    id: 'course-questions',
    label: 'Course Questions',
    icon: 'ClipboardDocumentCheckIcon',
    color: 'text-green-600',
    items: [
      {
        question: 'What is the dress code on the course?',
        answer:
          'Smart golf attire is required at all times. Collared shirts, tailored shorts or trousers, and golf shoes are expected. Denim, vest tops, and non-golf footwear are not permitted on the course or in the clubhouse.',
      },
      {
        question: 'What is the pace of play policy?',
        answer:
          'We ask all players to maintain a pace of 4 hours 15 minutes for 18 holes. Slow groups may be asked to allow faster groups to play through. Please be ready to play when it is your turn.',
      },
      {
        question: 'Are caddies or carts available?',
        answer:
          'Golf carts (buggies) are available for hire at the pro shop, subject to availability. Caddies are available on request — please enquire at least 48 hours in advance. Walking the course is always welcome.',
      },
      {
        question: 'What handicap is required to play?',
        answer:
          'There is no minimum handicap requirement for casual rounds. For club competitions, a valid Handicap Index registered with Golf South Africa (GSA) is required. Visitors may play social rounds without a handicap.',
      },
      {
        question: 'Are children allowed on the course?',
        answer:
          'Junior golfers (under 18) are welcome and encouraged. Juniors under 14 must be accompanied by an adult. Junior membership is available at a reduced rate for those wishing to play regularly.',
      },
      {
        question: 'What are the rules around mobile phones on the course?',
        answer:
          'Mobile phones must be kept on silent during play. Calls should be taken away from other players. Photography is welcome for personal use, but please be considerate of other golfers on the course.',
      },
    ],
  },
];

function highlight(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-accent/30 text-foreground rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function FAQAccordionItem({
  item,
  index,
  searchQuery,
  defaultOpen,
}: {
  item: FAQItem;
  index: number;
  searchQuery: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  React.useEffect(() => {
    if (defaultOpen !== undefined) setOpen(defaultOpen);
  }, [defaultOpen]);

  return (
    <div
      className={`border border-border rounded-xl overflow-hidden transition-all duration-200 ${
        open ? 'shadow-md' : 'hover:border-secondary/40'
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left bg-card hover:bg-muted/40 transition-colors duration-150"
        aria-expanded={open}
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-xs font-bold text-muted-foreground/60 tabular-nums w-5 shrink-0">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="text-sm font-semibold text-foreground leading-snug">
            {highlight(item.question, searchQuery)}
          </span>
        </div>
        <span
          className={`shrink-0 mt-0.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <Icon name="ChevronDownIcon" size={18} variant="outline" className="text-muted-foreground" />
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 bg-card border-t border-border/50">
          <p className="text-sm text-muted-foreground leading-relaxed pl-8">
            {highlight(item.answer, searchQuery)}
          </p>
        </div>
      )}
    </div>
  );
}

function FAQSection({
  category,
  filteredItems,
  searchQuery,
}: {
  category: FAQCategory;
  filteredItems: FAQItem[];
  searchQuery: string;
}) {
  if (filteredItems.length === 0) return null;

  return (
    <div id={category.id} className="scroll-mt-24">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
            <Icon name={category.icon as any} size={18} variant="outline" className={category.color} />
          </div>
          <h2 className="text-lg font-bold text-foreground">{category.label}</h2>
          <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {filteredItems.length} {filteredItems.length === 1 ? 'question' : 'questions'}
          </span>
        </div>
      </div>
      <div className="space-y-2">
        {filteredItems.map((item, i) => (
          <FAQAccordionItem
            key={i}
            item={item}
            index={i}
            searchQuery={searchQuery}
            defaultOpen={searchQuery.trim().length > 0}
          />
        ))}
      </div>
    </div>
  );
}

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState<string>('visitor-booking');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return faqCategories.map((cat) => ({ ...cat, filteredItems: cat.items }));
    const q = searchQuery.toLowerCase();
    return faqCategories.map((cat) => ({
      ...cat,
      filteredItems: cat.items.filter(
        (item) =>
          item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q)
      ),
    }));
  }, [searchQuery]);

  const totalResults = useMemo(
    () => filteredCategories.reduce((sum, cat) => sum + cat.filteredItems.length, 0),
    [filteredCategories]
  );

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <PublicNav />
      <main className="flex-1">
        {/* Hero */}
        <div className="gradient-primary py-14 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-medium px-3 py-1.5 rounded-full mb-4">
              <Icon name="QuestionMarkCircleIcon" size={14} variant="outline" />
              Help Centre
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 text-balance">
              Frequently Asked Questions
            </h1>
            <p className="text-white/70 text-base leading-relaxed max-w-xl mx-auto mb-8">
              Everything you need to know about booking a round, payments, membership, and playing at Maritzburg Golf Club.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <Icon name="MagnifyingGlassIcon" size={18} variant="outline" className="text-white/50" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions — e.g. cancellation, handicap, refund…"
                className="w-full pl-11 pr-10 py-3.5 rounded-xl bg-white/15 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent/60 transition-all duration-150 backdrop-blur-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-3 flex items-center px-1 text-white/50 hover:text-white transition-colors"
                  aria-label="Clear search"
                >
                  <Icon name="XMarkIcon" size={16} variant="outline" />
                </button>
              )}
            </div>

            {/* Search result count */}
            {isSearching && (
              <p className="mt-3 text-sm text-white/60">
                {totalResults > 0 ? (
                  <>
                    Found <span className="text-accent font-semibold">{totalResults}</span>{' '}
                    {totalResults === 1 ? 'result' : 'results'} for &ldquo;{searchQuery}&rdquo;
                  </>
                ) : (
                  <>No results for &ldquo;{searchQuery}&rdquo;</>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Category Nav — hidden during search */}
        {!isSearching && (
          <div className="sticky top-16 z-30 bg-card border-b border-border shadow-sm">
            <div className="max-w-4xl mx-auto px-4">
              <div className="flex gap-1 overflow-x-auto py-3 scrollbar-thin">
                {faqCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      const el = document.getElementById(cat.id);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-150 shrink-0 ${
                      activeCategory === cat.id
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={cat.icon as any} size={15} variant="outline" />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FAQ Content */}
        <div className="max-w-4xl mx-auto px-4 py-10 space-y-12">
          {/* No results state */}
          {isSearching && totalResults === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="MagnifyingGlassIcon" size={28} variant="outline" className="text-muted-foreground" />
              </div>
              <h3 className="text-base font-bold text-foreground mb-2">No results found</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                We couldn&apos;t find anything matching &ldquo;{searchQuery}&rdquo;. Try different keywords or browse the categories below.
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-sm font-medium text-foreground hover:bg-muted/80 transition-colors"
              >
                <Icon name="XMarkIcon" size={14} variant="outline" />
                Clear search
              </button>
            </div>
          )}

          {/* FAQ sections */}
          {filteredCategories.map((cat) => (
            <FAQSection
              key={cat.id}
              category={cat}
              filteredItems={cat.filteredItems}
              searchQuery={searchQuery}
            />
          ))}

          {/* Still need help CTA */}
          {(!isSearching || totalResults > 0) && (
            <div className="rounded-2xl bg-primary/5 border border-primary/15 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-base font-bold text-foreground mb-1">Still have questions?</h3>
                <p className="text-sm text-muted-foreground">
                  Our pro shop team is available Mon–Fri 6:30am–5pm and weekends 6am–5pm.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <a
                  href="tel:0333962356"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <Icon name="PhoneIcon" size={15} variant="outline" className="text-secondary" />
                  033 396 2356
                </a>
                <Link
                  href="/online-booking"
                  className="btn-gold px-5 py-2.5 rounded-lg text-sm text-center"
                >
                  Book a Round
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
